import type { LevelSim } from "@highmarch/domain";

/**
 * How far the points are from the last card offer's threshold to the next one, 0..1 (QoL I1). The thresholds are
 * cumulative (DESIGN §4.4: `offerThreshold(n)`); a pick's delay moves the next one, so the share is
 * clamped. A gap that has closed (the offer is due) reads as full. A threshold with nothing due passes without an
 * offer, so "the next one" is the next offer that will show (`LevelSim.nextShownOfferAt`).
 */
export function nextCardProgress(points: number, from: number, to: number): number {
  if (!(to > from)) return 1;
  return Math.max(0, Math.min(1, (points - from) / (to - from)));
}

/** Progress is written to the ring in steps this fine (a quarter percent): a kill always moves it, a frame never does. */
const RING_STEPS = 400;

/**
 * The NEXT CARD pill (QoL I1): the points still to go instead of the raw threshold, and a ring round the card icon
 * filling toward the next offer. Reads the points the HUD shows (kills whose rounds are still in the air are not in
 * them yet), so the ring and the points pill move together. Writes only on a change.
 */
export class NextCardView {
  /** The offer (by `offerCounter`) the ring counts from: thresholds passed with nothing due do not restart it. */
  private shown = -1;
  private from = 0;
  private shownToGo = -1;
  private shownStep = -1;

  constructor(private readonly count: HTMLElement, private readonly ring: HTMLElement) {}

  refresh(sim: LevelSim, points: number): void {
    if (sim.offerCounter !== this.shown) {                   // the threshold the last offer shown opened at (it loops)
      this.shown = sim.offerCounter;
      this.from = sim.balance.cards.offerThreshold(sim.offersOpened);
    }
    const to = sim.nextShownOfferAt;
    const toGo = Math.max(0, to - points);
    if (toGo !== this.shownToGo) { this.shownToGo = toGo; this.count.textContent = String(toGo); }
    const step = Math.round(nextCardProgress(points, this.from, to) * RING_STEPS);
    if (step === this.shownStep) return;
    this.shownStep = step;
    this.ring.style.setProperty("--p", (step / RING_STEPS).toFixed(4));
  }
}
